import { UserId } from '@business/entities/account';
import { ContactId } from '@business/entities/contact';
import { OrderProductResponse, OrderShippingOption, OrderTermId } from '@business/entities/order';
import { OrgId } from '@business/entities/organization';
import { FormArray, formArray, FormControl, formControl, FormDataType, FormGroup, formGroup, FormGroupData, Validators } from '@core/utils/form';
import { defineInvoiceSalesTaxForm } from '@modules/common/InvoiceSalesTax';
import { t } from 'ttag';
import { validateOrderAddress } from './AddEditTimeMaterialPage';
import { LabourTypeId } from '@business/entities/labour-type';
import { OrderEstimateStatus, TMSectionId } from '@business/entities/tm';
import { TMBoardId } from '@business/entities/tm/TimeMaterialBoardResponse';
import { defineProposalDiscountForm, defineTMFeeForm } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';

export function defineLabourForm() {
  return formGroup({
    labourTypeId: formControl<LabourTypeId>({}),
    hour: formControl<number>({
      validators: [Validators.min(0)],
    }),
    cost: formControl<number>({
      validators: [Validators.min(0)],
    }),
    price: formControl<number>({
      validators: [Validators.min(0)],
    }),
  });
}

export type LaborItemsFormType = FormGroup<
  {
    labourTypeId: FormControl<string>;
    hour: FormControl<number>;
    cost: FormControl<number>;
    price: FormControl<number>;
  },
  FormGroupData<{
    labourTypeId: FormControl<string>;
    hour: FormControl<number>;
    cost: FormControl<number>;
    price: FormControl<number>;
  }>
>;

export type LineItemFormType = FormGroup<
  {
    description: FormControl<string>;
    quantity: FormControl<number>;
    rate: FormControl<number>;
    classId: FormControl<string>;
    taxable: FormControl<boolean>;
    sectionId: FormControl<number | undefined>;
    labour: FormControl<number | undefined>;
    labourType: FormControl<string | undefined>;
    labourPrice: FormControl<number | undefined>;
    labourCost: FormControl<number | undefined>;
    labourItems: FormArray<LaborItemsFormType>;
    productLabourItems: FormArray<LaborItemsFormType>;
    cost: FormControl<number>;
    id: FormControl<number | string>;
    product: FormControl<string>;
    packageItems: FormArray<LineItemFormType>;
    vendorId: FormControl<string>;
    tierId: FormControl<number | string>;
    lineTitle: FormControl<string>;
    unitOfMeasure: FormControl<string>;
    lineProduct: FormControl<OrderProductResponse | null>;
    productImage: FormControl<string | File | undefined>;
    isDiscount: FormControl<boolean>;
    isServicePhase: FormControl<boolean>;
  },
  FormGroupData<{
    description: FormControl<string>;
    quantity: FormControl<number>;
    rate: FormControl<number>;
    classId: FormControl<string>;
    taxable: FormControl<boolean>;
    sectionId: FormControl<number | undefined>;
    labour: FormControl<number | undefined>;
    labourType: FormControl<string | undefined>;
    labourPrice: FormControl<number | undefined>;
    labourCost: FormControl<number | undefined>;
    labourItems: FormArray<LaborItemsFormType>;
    productLabourItems: FormArray<LaborItemsFormType>;
    cost: FormControl<number>;
    id: FormControl<number | string>;
    product: FormControl<string>;
    packageItems: FormArray<LineItemFormType>;
    vendorId: FormControl<string>;
    tierId: FormControl<number | string>;
    lineTitle: FormControl<string>;
    unitOfMeasure: FormControl<string>;
    lineProduct: FormControl<OrderProductResponse | null>;
    productImage: FormControl<string | File | undefined>;
    isDiscount: FormControl<boolean>;
    isServicePhase: FormControl<boolean>;
  }>
>;

export const defineLineItemForm = (classIdRequired?: boolean): LineItemFormType => {
  return formGroup({
    packageItems: formArray(() => defineLineItemForm(classIdRequired)),
    product: formControl<string>({ required: false, placeholder: 'Search Product' }),
    description: formControl<string>({ required: true, placeholder: 'Description' }),
    quantity: formControl<number>({ placeholder: '0', initialValue: 1, required: true }),
    rate: formControl<number>({ placeholder: '0.00', initialValue: 0 }),
    cost: formControl<number>({ placeholder: '0.00', initialValue: 0 }),
    classId: formControl<string>({ placeholder: 'Class', required: classIdRequired }),
    vendorId: formControl<string>({ placeholder: 'Supplier', disabled: true }),
    tierId: formControl<number | string>({ placeholder: 'Tier' }),
    id: formControl<number | string>(),
    lineTitle: formControl<string>(),
    unitOfMeasure: formControl<string>(),
    lineProduct: formControl<OrderProductResponse | null>(),
    taxable: formControl<boolean>({ initialValue: false }),
    labour: formControl<number | undefined>({ required: false }),
    labourType: formControl<LabourTypeId | undefined>({ required: false }),
    labourPrice: formControl<number | undefined>({ required: false }),
    labourCost: formControl<number | undefined>({ required: false }),
    labourItems: formArray(() => defineLabourForm()),
    productLabourItems: formArray(() => defineLabourForm()),
    sectionId: formControl<TMSectionId | undefined>({ required: false }),
    productImage: formControl<string | File | undefined>(),
    isDiscount: formControl<boolean>({ initialValue: false }),
    isServicePhase: formControl<boolean>({ initialValue: false }),
  });
};

export const defineTMSectionForm = () => {
  return formGroup({
    id: formControl<number>({ required: true, initialValue: 0 }),
    title: formControl<string>({ required: true, placeholder: 'Section Title' }),
    menuOrder: formControl<number>({ required: true, initialValue: 0 }),
    checked: formControl<boolean>({ initialValue: false }),
  });
};

export type TMSectionFormData = FormDataType<typeof defineTMSectionForm>;
export type TMSectionFormType = ReturnType<typeof defineTMSectionForm>;

export const defineOrderForm = (classIdRequired?: boolean, isInvoice?: boolean) => {
  return formGroup({
    orgId: formControl<OrgId>({ required: true, label: 'Select Organization' }),
    contact: formControl<ContactId>({ required: true, label: 'Contact' }),
    name: formControl<string>({ label: 'Name' }),
    orderNumber: formControl<string>({ label: 'Work Order Number' }),
    salesReps: formControl<UserId[]>({ required: true, label: 'Salesperson' }),
    termId: formControl<OrderTermId>({ required: false, label: 'Terms' }),
    billingEmail: formControl<string>({ label: 'Email', required: true, validators: [Validators.emails, Validators.maxLength(100)] }),
    billingAddress: formControl<string>({ label: 'Billing Address', required: true, validators: [Validators.dynamic(validateOrderAddress)] }),
    shippingAddress: formControl<string>({ label: 'Shipping Address', required: true, validators: [Validators.dynamic(validateOrderAddress)] }),
    shippingOption: formControl<OrderShippingOption>({ label: 'Shipping Options', required: true, initialValue: OrderShippingOption.ready }),
    memo: formControl<string>({ label: 'Statement memo' }),
    message: formControl<string>({ label: 'Notes', initialValue: 'Thank you for your business and have a great day!' }),
    lineItems: formArray(() => defineLineItemForm(classIdRequired)),
    status: formControl<OrderEstimateStatus>({
      initialValue: OrderEstimateStatus.sent,
    }),
    type: formControl<string>({
      initialValue: 'invoice',
    }),
    discount: defineProposalDiscountForm(),
    salesTax: defineInvoiceSalesTaxForm(),

    customerPoNo: formControl<string>({ label: 'Customer PO#' }),
    shippingMethod: formControl<string>({ placeholder: 'Shipping Via', label: 'Shipping Via' }),
    trackId: formControl<string>({ placeholder: 'Tracking #', label: 'Tracking #', validators: [Validators.maxLength(30)] }),
    shippedDate: formControl<number>({ placeholder: 'Ship Date', label: 'Ship Date' }),
    isHide: formControl<boolean>({ label: t`Hide from Board`, initialValue: false }),
    invoiceDate: formControl<number>({ label: t`Invoice Date` }),
    dueDate: formControl<number>({ label: t`Due Date` }),
    sections: formArray(() => defineTMSectionForm(), {
      initialValue: [{ id: -1, title: 'Default Room', menuOrder: 1, checked: false }],
    }),
    boardId: formControl<TMBoardId>({ label: 'Select Board', required: isInvoice ? false : true }),
    fees: formArray(() => defineTMFeeForm()),
  });
};

export type OrderFormData = FormDataType<typeof defineOrderForm>;
export type OrderFormType = ReturnType<typeof defineOrderForm>;

export type OrderLineItemForm = ReturnType<typeof defineLineItemForm>;
export type OrderLineItemData = FormDataType<typeof defineLineItemForm>;

export type OrderLabourItemForm = ReturnType<typeof defineLabourForm>;
export type OrderLabourItemData = FormDataType<typeof defineLabourForm>;
